import { formatAssetClassLabel, isTradedAssetClassesField } from "./tradingViewBroker";

export { formatAssetClassLabel, isTradedAssetClassesField };

export const CTRADER_BROKER_SLUG = "ctrader";

export const CTRADER_DEFAULT_ASSET_CLASSES = [
  "commodity",
  "crypto",
  "energy",
  "equity",
  "fx",
  "index",
  "metal",
] as const;

export const CTRADER_UTC_OFFSET_FIELD = "utc_offset_hours";

export function isCtraderBroker(brokerName: string | null | undefined): boolean {
  return String(brokerName || "").toLowerCase() === CTRADER_BROKER_SLUG;
}

export function isCtraderUtcOffsetField(field: string): boolean {
  return field.toLowerCase() === CTRADER_UTC_OFFSET_FIELD;
}

/** Hidden on import — always sent as `ctrader`. */
export function isCtraderHiddenImportField(field: string): boolean {
  return field.toLowerCase() === "broker_name";
}

export function isCtraderHiddenCredentialField(field: string): boolean {
  const lower = field.toLowerCase();
  return lower === "broker_name" || lower === "traded_asset_classes";
}

export function buildCtraderConnectCredentialPayload(
  assetClasses: string[],
  executingBrokerName?: string,
  accountCode?: string
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    broker_name: CTRADER_BROKER_SLUG,
    traded_asset_classes: assetClasses.map((c) => c.trim().toLowerCase()).filter(Boolean),
  };
  const account = String(accountCode || "").trim();
  if (account) {
    payload.account_code = account;
  }
  const broker = String(executingBrokerName || "").trim();
  if (broker) {
    payload.executing_broker_name = broker;
  }
  return payload;
}

/** Resolve Tessera account code from credential / CSV form values. */
export function resolveCtraderAccountCodeFromValues(
  values: Record<string, string | undefined>
): string {
  return (
    (values.account_code || values.accountCode || values.account_name || "").trim()
  );
}

export const CTRADER_IMPORT_FIELD_LABELS: Record<string, string> = {
  account_code: "Account number (login)",
  traded_asset_classes: "Asset classes",
  executing_broker_name: "Executing broker",
  [CTRADER_UTC_OFFSET_FIELD]: "UTC offset",
};

export function ctraderImportFieldLabel(field: string, fallback: string): string {
  return CTRADER_IMPORT_FIELD_LABELS[field] ?? fallback;
}

export function buildCtraderCsvImportParams(
  values: Record<string, string>,
  assetClasses: string[]
): Record<string, string | string[]> {
  const accountCode = (values.account_code || values.accountCode || "").trim();
  const executingBrokerName = (
    values.executing_broker_name ||
    values.executingBrokerName ||
    ""
  ).trim();
  const params: Record<string, string | string[]> = {
    broker_name: CTRADER_BROKER_SLUG,
    account_code: accountCode,
    traded_asset_classes: assetClasses.map((c) => c.trim()).filter(Boolean),
  };
  if (executingBrokerName) {
    params.executing_broker_name = executingBrokerName;
  }
  const offsetRaw = (values[CTRADER_UTC_OFFSET_FIELD] ?? "").trim();
  if (offsetRaw !== "") {
    const offset = Number(offsetRaw);
    if (Number.isFinite(offset)) {
      params[CTRADER_UTC_OFFSET_FIELD] = String(offset);
    }
  }
  return params;
}

export function isCtraderCsvFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".csv");
}

export function parseCtraderSavedAssetClasses(
  credentials: Record<string, unknown> | null | undefined
): string[] {
  if (!credentials) return [];
  const raw =
    credentials.traded_asset_classes ??
    credentials.tradedAssetClasses ??
    credentials.asset_classes;
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v).trim().toLowerCase()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(/[,;]+/)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

export function formatCtraderImportSuccessDescription(
  payload: Record<string, unknown> | null | undefined
): string | null {
  const meta = (payload?.report_meta ?? payload?.reportMeta) as Record<string, unknown> | undefined;
  const dealsCount = Number(meta?.deals_count ?? meta?.dealsCount);
  const stats = (payload?.sync_stats ?? payload?.syncStats) as Record<string, unknown> | undefined;
  const created = stats?.created ?? stats?.Created;
  const updated = stats?.updated ?? stats?.Updated;

  if (Number.isFinite(dealsCount) && dealsCount > 0) {
    let line = `Imported ${dealsCount} trade${dealsCount === 1 ? "" : "s"}.`;
    if (typeof created === "number" || typeof updated === "number") {
      const parts: string[] = [];
      if (typeof created === "number") parts.push(`${created} new`);
      if (typeof updated === "number") parts.push(`${updated} updated`);
      if (parts.length) line += ` ${parts.join(", ")}.`;
    }
    return line;
  }
  return null;
}

export function formatCtraderReconciliationWarning(
  payload: Record<string, unknown> | null | undefined
): string | null {
  if (!payload || payload.reconciliation_ok !== false) return null;
  const meta = (payload.report_meta ?? payload.reportMeta) as Record<string, unknown> | undefined;
  const summaryPl = meta?.summary_realised_pl ?? meta?.summaryRealisedPl;
  const dealsNet = meta?.deals_net_sum ?? meta?.dealsNetSum;
  if (summaryPl != null && dealsNet != null) {
    return `Statement totals don't match imported trades (expected ${summaryPl}, got ${dealsNet}). Check the file or contact support.`;
  }
  return "Statement totals don't match imported trades. Check the file or contact support.";
}

export function formatCtraderImportDetailLines(
  payload: Record<string, unknown> | null | undefined
): string[] {
  const meta = (payload?.report_meta ?? payload?.reportMeta) as Record<string, unknown> | undefined;
  const lines: string[] = [];
  const offset = meta?.utc_offset_hours ?? meta?.utcOffsetHours ?? payload?.utc_offset_hours;
  if (offset != null && String(offset).trim() !== "") {
    const n = Number(offset);
    const label = Number.isFinite(n) && n >= 0 ? `UTC+${n}` : `UTC${offset}`;
    lines.push(`Offset used: ${label}`);
  }
  const currency = meta?.account_currency ?? meta?.accountCurrency;
  if (currency != null && String(currency).trim()) {
    lines.push(`Account currency: ${String(currency).trim()}`);
  }
  return lines;
}
